import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { FeatureFlagService } from '@innovation-os-ng/core/core';
import { LogService } from '@innovation-os-ng/core/logger';
import {
  ControlPropertyValue,
  EntityConfigControl,
  EntityConfigControlChild,
  EntityConfigControlInput,
  EntityConfigControlInputV2,
  EntityConfigField,
  MappedEntityConfigControl,
  PropertySuffix,
  VANILLA
} from '@innovation-os-ng/core/shared';
import { SpaceSelectors, skipper } from '@innovation-os-ng/core/state';
import { EnvironmentService } from '@innovation-os-ng/core/utils';
import { Store } from '@ngrx/store';
import { Observable, ReplaySubject, Subject, of, take } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ITONICS_URIS } from '../constants/itonicsUri';
import { EntityConfigFieldService } from './entity-config-field.service';
import { ErrorUIType, ErrorUiService } from './error-ui.service';

@Injectable({
  providedIn: 'root'
})
export class EntityConfigControlService implements OnDestroy {
  private apiUrl: string = 'elements-config/controls';

  private spaceControls: {
    [spaceUri: string]: ReplaySubject<Array<EntityConfigControl>>;
  } = {};

  private spaceMappedControls: {
    [space: string]: MappedEntityConfigControl;
  } = {};

  private onDestroy: Subject<void> = new Subject<void>();

  private mappedFeatureFlags: {
    [featureFlagId: string]: string;
  } = {};

  private spaceControlMappedWithFieldUri: {
    [spaceUri: string]: {
      [fieldUri: string]: EntityConfigControl;
    };
  } = {};

  private currentspaceUri: string;

  constructor(
    private readonly http: HttpClient,
    private readonly errorUiService: ErrorUiService,
    private readonly environmentService: EnvironmentService,
    private readonly logger: LogService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly entityConfigFieldService: EntityConfigFieldService,
    private readonly store: Store
  ) {
    this.store
      .select(SpaceSelectors.selectCurrentSpaceUri)
      .pipe(skipper())
      .subscribe({
        next: (currentspaceUri) => {
          this.currentspaceUri = currentspaceUri;
          this.updateFeatureFlagMap();
        }
      });
  }

  private async updateFeatureFlagMap(): Promise<void> {
    const featureFlagsIds = await this.featureFlagService.getFeatureFlags();
    this.mappedFeatureFlags = featureFlagsIds.reduce((mappedObj, featureFlagId) => ({ ...mappedObj, [featureFlagId]: featureFlagId }), {});
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  /**
   * Provide controls from space
   *
   * @param spaceUri
   * @param freshFetch - Won't check cache existence and force fresh fetch control
   * @returns controls in space
   */
  public getControls(spaceUri: string = this.currentspaceUri, freshFetch: boolean = false): Observable<Array<EntityConfigControl>> {
    console.assert(!!this.currentspaceUri, 'currentSpaceUri should not be null');
    if (!this.spaceControls[spaceUri] || freshFetch) {
      this.loadSpaceControls(spaceUri);
    }

    return this.spaceControls[spaceUri].asObservable();
  }

  /**
   * Provides control information of provided space uri check in the cache first if
   * not found then fetch the controls and return the newly generated mapped values
   */
  public getMappedControlsBySpaceUri(spaceUri: string): Observable<MappedEntityConfigControl> {
    if (!spaceUri) {
      return;
    }

    // Check-in cache
    if (this.spaceMappedControls[spaceUri]) {
      return of(this.spaceMappedControls[spaceUri]);
    }

    // Otherwise fetch controls and return the mapped controls
    return this.getControls(spaceUri).pipe(
      map((controls: Array<EntityConfigControl>) =>
        controls.reduce((mappedControls: MappedEntityConfigControl, control: EntityConfigControl) => {
          mappedControls[control.uri] = control;
          return mappedControls;
        }, {})
      )
    );
  }

  /**
   * Checks for control detail information in the cache and provides it.
   */
  public getControl(controlUri: string): EntityConfigControl {
    console.assert(!!this.currentspaceUri, 'currentSpaceUri should not be null');
    const activeSpace: string = this.currentspaceUri;
    return this.spaceMappedControls[activeSpace][controlUri];
  }

  /**
   *  Get control from fieldUri
   * @param fieldUri {string} - string
   * @returns {EntityConfigControl}
   */
  public getControlFromFieldUri(fieldUri: string): Observable<EntityConfigControl> {
    console.assert(!!this.currentspaceUri, 'currentSpaceUri should not be null');
    const activeSpace: string = this.currentspaceUri;
    if (this.spaceControlMappedWithFieldUri[activeSpace]?.[fieldUri]) {
      return of(this.spaceControlMappedWithFieldUri[activeSpace][fieldUri]);
    }
    return this.getControls(activeSpace).pipe(map(() => this.spaceControlMappedWithFieldUri[activeSpace][fieldUri]));
  }

  /**
   * Checks for control detail information in the cache and provides it.
   * If not found, fetches the controls and returns
   */
  public getControlBySpaceUri(controlUri: string, spaceUri: string): Observable<EntityConfigControl> {
    return this.getMappedControlsBySpaceUri(spaceUri).pipe(map((mappedControls: MappedEntityConfigControl) => mappedControls[controlUri]));
  }

  /**
   * Create and save new control
   */
  public saveControl(control: EntityConfigControlInput, spaceUri: string): Observable<EntityConfigControl> {
    const url: string = this.environmentService.getRestUrl(`${this.apiUrl}`);

    return this.http.post<Array<EntityConfigControl>>(url, control).pipe(
      map(([savedControl]: Array<EntityConfigControl>) => savedControl),
      tap({
        next: (savedControl: EntityConfigControl) => {
          // Taking the controls of provided space uri from the cache
          this.spaceControls[spaceUri].pipe(take(1)).subscribe((cachedControls = []) => {
            // Append the newly saved control and emit the updated value to update
            // cache and also get notified if any components are actively listing this observable.
            this.spaceControls[spaceUri].next([...cachedControls, savedControl]);
          });

          // Update the control map
          if (savedControl?.uri && this.spaceMappedControls[spaceUri]) {
            this.spaceMappedControls[spaceUri] = { ...this.spaceMappedControls[spaceUri], [savedControl.uri]: savedControl };
          }

          // Update the control field map
          if (savedControl?.field) {
            this.spaceControlMappedWithFieldUri[spaceUri][savedControl.field.uri] = savedControl;
            // Update field cache
            this.entityConfigFieldService.updateFieldCache(savedControl.field, spaceUri);
          }
        },
        error: (error) => {
          this.logger.warn('EntityConfigControlService: Failed to save control', { error, control });
        }
      })
    );
  }

  /**
   *
   * Delete the control
   */
  public deleteControl(control: EntityConfigControl): Observable<EntityConfigControl> {
    const url: string = this.environmentService.getRestUrl(`${this.apiUrl}/latest/${control.uri}`);

    return this.http.delete<Array<EntityConfigControl>>(url).pipe(
      map(([deletedControl]: Array<EntityConfigControl>) => deletedControl),
      tap({
        next: (deletedControl: EntityConfigControl) => {
          // Remove the deleted control and its descendants from cache
          if (this.spaceControls[deletedControl.spaceUri]) {
            this.spaceControls[deletedControl.spaceUri].pipe(take(1)).subscribe((cachedControls) => {
              // Filter out the deleted control and its descendants
              const updateControls = cachedControls.filter((cachedControl) => {
                if (cachedControl.uri === deletedControl.uri) {
                  // Exclude the deleted control
                  return false;
                }
                if (cachedControl?.descendants?.length) {
                  // Exclude descendants of the deleted control
                  cachedControl.descendants = cachedControl.descendants.filter((descendant) => descendant.uri !== deletedControl.uri);
                }
                return true;
              });
              this.spaceControls[deletedControl.spaceUri].next(updateControls);
            });

            // Update the map control
            if (
              deletedControl?.uri &&
              this.spaceMappedControls[deletedControl.spaceUri] &&
              this.spaceMappedControls[deletedControl.spaceUri][deletedControl.uri]
            ) {
              const { [deletedControl.uri]: _, ...updatedSpaceMappedControls } = this.spaceMappedControls[deletedControl.spaceUri];
              this.spaceMappedControls[deletedControl.spaceUri] = updatedSpaceMappedControls;
            }
          }
        },
        error: (error) => {
          this.logger.warn('EntityConfigControlService: Failed to delete control', { error, control });
        }
      })
    );
  }

  /**
   * Update the control
   */
  public updateControl(control: EntityConfigControlInputV2): Observable<EntityConfigControl> {
    const url: string = this.environmentService.getRestUrl(`${this.apiUrl}/latest/${control.uri}`);

    return this.http.put<Array<EntityConfigControl>>(url, control).pipe(
      map(([updateControl]: Array<EntityConfigControl>) => updateControl),
      tap({
        next: (updatedControl: EntityConfigControl) => {
          // Update the control in cache if it exists
          if (this.spaceControls[updatedControl.spaceUri]) {
            this.spaceControls[updatedControl.spaceUri].pipe(take(1)).subscribe((cachedControls) => {
              const updatedControls = cachedControls.map((cachedControl) =>
                cachedControl.uri === updatedControl.uri ? updatedControl : cachedControl
              );
              const controlsWithRevisedProperties: Array<EntityConfigControl> = this.attachDescendentControls(
                updatedControl.spaceUri,
                updatedControls
              );
              this.spaceControls[updatedControl.spaceUri].next(controlsWithRevisedProperties);
            });
          }

          // Update the map
          if (
            updatedControl?.uri &&
            this.spaceMappedControls[updatedControl.spaceUri] &&
            this.spaceMappedControls[updatedControl.spaceUri][updatedControl.uri]
          ) {
            this.spaceMappedControls[updatedControl.spaceUri] = {
              ...this.spaceMappedControls[updatedControl.spaceUri],
              [updatedControl.uri]: updatedControl
            };
          }

          if (updatedControl?.field) {
            // Update field cache
            this.entityConfigFieldService.updateFieldCache(updatedControl.field, updatedControl.spaceUri);
          }
        },
        error: (error) => {
          this.logger.warn('EntityConfigControlService: Failed to update control', { error });
        }
      })
    );
  }

  /**
   * Fetch all controls from the provided spaceUri and update cache
   */
  private loadSpaceControls(spaceUri: string) {
    if (!this.spaceControls[spaceUri]) {
      this.spaceControls[spaceUri] = new ReplaySubject<Array<EntityConfigControl>>(1);
    }
    const url: string = this.environmentService.getRestUrl(`${this.apiUrl}/space/${spaceUri}`);
    this.http
      .get<Array<EntityConfigControl>>(url)
      .pipe(
        take(1),
        // Adding descendant children on controls [In new EC backend won't provide descendants information due the costly operation]
        map((controls: Array<EntityConfigControl>) => {
          return this.attachDescendentControls(spaceUri, controls);
        }),
        map((controls) => {
          // Create a map of fieldUri and control
          const controlMappedWithFieldUri = controls.reduce((controlMapWithFieldUri, control) => {
            // only map the controls having field values [leaf controls]
            if (control.field) {
              controlMapWithFieldUri[control.field.uri] = control;
            }
            return controlMapWithFieldUri;
          }, {});
          this.spaceControlMappedWithFieldUri[spaceUri] = controlMappedWithFieldUri;
          return controls;
        })
      )
      .subscribe({
        next: (controls: Array<EntityConfigControl>) => {
          if (!controls) {
            this.spaceControls[spaceUri].next([]);
            return;
          }
          this.spaceControls[spaceUri].next(controls);
        },
        error: (err) => {
          this.errorUiService.notifyError({
            type: ErrorUIType.REQUEST,
            value: err
          });

          this.spaceControls[spaceUri].next([]);
        }
      });
  }

  /**
   * Check the control is disabled by user or feature flag
   */
  private isControlDisabled(childControl: EntityConfigControlChild, mappedControl: MappedEntityConfigControl): boolean {
    if (childControl.disabled) {
      return true;
    }

    const control = mappedControl[childControl.uri];

    const featureFlagProperty: ControlPropertyValue = control?.propertyValues?.find((p) =>
      p.controlTypePropertyUri.endsWith(PropertySuffix.FEATURE_FLAG)
    );

    if (!featureFlagProperty) {
      return childControl.disabled;
    }

    return !Object.prototype.hasOwnProperty.call(this.mappedFeatureFlags, featureFlagProperty.value);
  }

  /**
   * Provide the flat descendants child controls
   */
  private getFlatDescendantsControls(controlUri: string, mappedControls: MappedEntityConfigControl): Array<EntityConfigControlChild> {
    let flatDescendantsControls: Array<EntityConfigControlChild> = [];

    const pushDescendants = (controlUri) => {
      const control = mappedControls[controlUri];
      if (control?.descendants.length) {
        // Check the control are disabled by user or feature flag and update the disabled property
        const descendantsControls = control.descendants.map((ctrl) => ({
          ...ctrl,
          disabled: this.isControlDisabled(ctrl, mappedControls)
        }));

        // Collect the all descendant controls in flatDescendantsControls variable
        flatDescendantsControls = [...flatDescendantsControls, ...descendantsControls];

        // Check the child descendants and call recursively
        if (control.descendants.length) {
          control.descendants.forEach((descendantCtrl) => {
            pushDescendants(descendantCtrl.uri);
          });
        }
      }
    };
    pushDescendants(controlUri);
    return flatDescendantsControls;
  }

  /**
   * check if the control has the itonics space uri
   * @param control
   * @returns
   */
  private hasItonicsSpaceUri(control: EntityConfigControl): boolean {
    return ITONICS_URIS.includes(control.spaceUri);
  }

  /**
   * check if the control is vanilla/default or not
   * @param control
   * @returns
   */
  public isItonicsControl(control: EntityConfigControl): boolean {
    return control?.tenantUri === VANILLA || control?.default;
  }

  /**
   * return field with default value assign based on the field's space uri
   * @param field
   * @returns
   */
  private assignDefaultProperty(field: EntityConfigField): EntityConfigField {
    if (!field) return null;
    return { ...field, default: this.entityConfigFieldService.hasItonicsSpaceUri(field) };
  }

  /**
   *  for updating the latest controls in cache
   * @param controls Array<EntityConfigControl>
   * @param spaceUri string
   *
   */
  public syncCacheControl(controls: Array<EntityConfigControl>, spaceUri: string): void {
    // set descendent to empty array if the newly added control does not have descendant
    controls = controls.map((control) => ({
      ...control,
      descendants: control.descendants || []
    }));
    if (this.spaceControls[spaceUri]) {
      this.spaceControls[spaceUri].pipe(take(1)).subscribe((cachedControls) => {
        this.spaceControls[spaceUri].next([...cachedControls, ...controls]);
      });
    }
  }

  /**
   * Attach descendent controls in each parent control
   *
   * @param { string } spaceUri - Space URI
   * @param { Array<EntityConfigControl> } controls - Controls List
   * @returns { Array<EntityConfigControl> } - Controls with descendant controls
   */
  private attachDescendentControls(spaceUri: string, controls: Array<EntityConfigControl>): Array<EntityConfigControl> {
    const mappedControls: MappedEntityConfigControl = controls.reduce(
      (mappedControls, control) => ({ ...mappedControls, [control.uri]: control }),
      {}
    );

    this.spaceMappedControls[spaceUri] = mappedControls;

    return controls.map((control: EntityConfigControl) => ({
      ...control,
      descendants: this.getFlatDescendantsControls(control.uri, mappedControls),
      // Assign default value to identify itonics type control in new EC
      default: this.hasItonicsSpaceUri(control),
      // Update field of the control with default value for identifying the itonics type field
      field: this.assignDefaultProperty(control.field)
    }));
  }
}
